import React, { useState, useEffect, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  onAuthStateChanged, 
  signOut, 
  signInWithPopup, 
  GoogleAuthProvider,
  signInAnonymously,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { 
  getFirestore, 
  doc, 
  setDoc, 
  updateDoc, 
  increment, 
  onSnapshot 
} from 'firebase/firestore';
import { 
  Star, Hash, Eye, Send, LogOut, ChevronRight, User, Edit3, Sparkles, Mail, X
} from 'lucide-react';

// ─── FIREBASE ─────────────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyDdjjpL_ffTU0re_bbcnarqCDWEV9QqX1I",
  authDomain: "mystica-aeterna.firebaseapp.com",
  projectId: "mystica-aeterna",
  storageBucket: "mystica-aeterna.firebasestorage.app",
  messagingSenderId: "332845281679",
  appId: "1:332845281679:web:d29693e1cc8cf851fa1bfa",
  measurementId: "G-5QQWE2R8W1"
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const googleProvider = new GoogleAuthProvider();

const APP_ID = "mystica-aeterna-prod";
const GEMINI_API_KEY = process.env.REACT_APP_GEMINI_API_KEY || "";

// ─── UTILITY ──────────────────────────────────────────────────────────────────
const getZodiacSign = (dob) => {
  if (!dob) return null;
  const date = new Date(dob);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return "Acquario";
  if ((month === 2 && day >= 19) || (month === 3 && day <= 20)) return "Pesci";
  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return "Ariete";
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return "Toro";
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return "Gemelli";
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return "Cancro";
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return "Leone";
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return "Vergine";
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return "Bilancia";
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return "Scorpione";
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return "Sagittario";
  return "Capricorno";
};

const formatDate = (dob) => {
  if (!dob) return '';
  const [year, month, day] = dob.split('-');
  return `${day}/${month}/${year}`;
};

const getLuckyNumbers = (dob, today) => {
  if (!dob || !today) return [7, 14, 28, 55, 73];
  const seed = (dob + today).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  let state = seed;
  const next = () => { state = (state * 1664525 + 1013904223) & 0x7fffffff; return state; };
  const numbers = new Set();
  while (numbers.size < 5) numbers.add((next() % 90) + 1);
  return Array.from(numbers).sort((a, b) => a - b);
};

// ─── SYSTEM PROMPTS ───────────────────────────────────────────────────────────
const getSystemPrompt = (tabId, nickname, zodiacSign, dob, today, luckyNumbers) => {
  const hasProfile = nickname && zodiacSign;
  const nameStr = hasProfile ? nickname : "Viandante";
  const signStr = hasProfile ? zodiacSign : "sconosciuto";

  switch (tabId) {
    case 'oroscopo':
      if (!hasProfile) return `Sei l'Oracolo di Mystica Aeterna. L'utente non ha ancora condiviso il suo segno zodiacale. Dai una lettura generale sull'energia del giorno di oggi, ${today}, in modo caldo e rassicurante. Invita gentilmente l'utente a condividere la sua data di nascita per una lettura più precisa. Massimo 2 paragrafi, circa 100 parole. Non usare emoji.`;
      return `Sei l'Oracolo di Mystica Aeterna, specializzato in astrologia. Rispondi a ${nameStr}, nato sotto il segno del ${signStr}, con tono caldo e rassicurante, come un'amica saggia che conosce le stelle. Oggi è ${today}. Scrivi esattamente 2 paragrafi brevi, circa 100 parole in totale. Il primo paragrafo descrive l'energia del giorno per il loro segno. Il secondo conclude con un consiglio pratico e positivo realizzabile oggi. Linguaggio semplice e diretto, niente termini esoterici complicati. Non dare mai consigli medici. Non fare mai previsioni negative dirette. Non usare emoji.`;
    case 'numeri':
      if (!hasProfile) return `Sei l'Oracolo di Mystica Aeterna, specializzato in numerologia. L'utente non ha ancora condiviso la sua data di nascita. Dai una lettura numerologica generale sull'energia del giorno di oggi, ${today}. Invita gentilmente l'utente a condividere la sua data di nascita per calcolare i suoi numeri personali. Massimo 2 paragrafi, circa 100 parole. Non usare emoji.`;
      return `Sei l'Oracolo di Mystica Aeterna, specializzato in numerologia. Rispondi a ${nameStr}, nato il ${formatDate(dob)}. Oggi è ${today}. I numeri fortunati di ${nameStr} per oggi sono: ${luckyNumbers.join(' - ')}. Questi 5 numeri, compresi tra 1 e 90, sono stati calcolati dalla sua data di nascita e dall'energia del giorno di oggi. Scrivi esattamente 2 paragrafi brevi, circa 100 parole in totale. Il primo paragrafo presenta i 5 numeri scrivendoli chiaramente, e spiega in modo semplice e positivo l'energia che li lega oggi. Il secondo paragrafo conclude con un incoraggiamento caldo. Linguaggio semplice e diretto. Non fare mai previsioni negative. Non usare emoji.`;
    case 'futuro':
      return `Sei l'Oracolo di Mystica Aeterna. Rispondi a ${nameStr}${hasProfile ? ` (${signStr})` : ''} che ti fa una domanda sul futuro. Oggi è ${today}. Usa sempre un linguaggio che suggerisce e orienta, mai che afferma con certezza: usa frasi come "le energie suggeriscono", "le stelle sembrano favorevoli", "potrebbe essere il momento di". Scrivi esattamente 2 paragrafi brevi, circa 100 parole in totale. Il primo interpreta la domanda in chiave energetica e positiva. Il secondo conclude con un consiglio pratico e incoraggiante. Non fare mai previsioni negative dirette. Non dare mai consigli medici o legali. Non usare emoji.`;
    default:
      return `Sei l'Oracolo di Mystica Aeterna. Rispondi in modo saggio, breve e rassicurante. Oggi è ${today}. Massimo 2 paragrafi, circa 100 parole. Concludi con un consiglio pratico. Non usare emoji.`;
  }
};

// ─── TABS ─────────────────────────────────────────────────────────────────────
const tabs = [
  { id: 'oroscopo', name: 'Oroscopo', desc: "L'energia delle stelle per la tua giornata", longDesc: 'Scopri cosa hanno in serbo le stelle per te oggi.', suggestions: ["Come sarà la mia giornata oggi?", "Cosa dicono le stelle per questa settimana?", "Quali energie mi circondano adesso?"], icon: Star, color: '#C9A84C', bg: '#FFF8EC' },
  { id: 'numeri', name: 'Numeri', desc: 'Il tuo numero del giorno e la sua energia', longDesc: 'Scopri la vibrazione numerica che guida oggi.', suggestions: ["Qual è il mio numero fortunato oggi?", "Cosa mi porta la vibrazione di oggi?", "Che energia numerica ho in questo periodo?"], icon: Hash, color: '#7C5CBF', bg: '#F3EEFF' },
  { id: 'futuro', name: 'Futuro', desc: "Fai una domanda, l'oracolo ti orienta", longDesc: 'Hai una domanda? Le stelle hanno una risposta.', suggestions: ["Come andrà questo colloquio di lavoro?", "Devo fidarmi di questa persona?", "È il momento giusto per cambiare?"], icon: Eye, color: '#2D9CDB', bg: '#EBF6FF' },
];

// ─── INPUT STYLE HELPER ───────────────────────────────────────────────────────
const inputStyle = { background: '#F8F7F4', border: '1.5px solid #E5E7EB', color: '#1A1A2E', borderRadius: '12px', padding: '12px', fontSize: '14px', outline: 'none', width: '100%', transition: 'border-color 0.2s' };

// ─── APP ──────────────────────────────────────────────────────────────────────
const App = () => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState({ messageCount: 0, nickname: '', dob: '', email: '' });
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('oroscopo');
  const chatEndRef = useRef(null);

  // Modali
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  // Cookie banner
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  // PWA install
  const [showPWABanner, setShowPWABanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Form profilo (nickname + data)
  const [profileData, setProfileData] = useState({ nickname: '', day: '', month: '', year: '' });

  // Form auth (email + password)
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register'
  const [authData, setAuthData] = useState({ email: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const currentTab = tabs.find(t => t.id === activeTab) || tabs[0];
  const TabIcon = currentTab?.icon || Star;

  // Auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u) { setUser(u); }
      else { signInAnonymously(auth).catch(console.error); }
    });
    return () => unsub();
  }, []);

  // Profilo utente
  useEffect(() => {
    if (!user) return;
    const ref = doc(db, 'artifacts', APP_ID, 'users', user.uid, 'settings', 'profile');
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setUserData(snap.data());
      } else {
        setDoc(ref, { messageCount: 0, nickname: '', dob: '', email: '' });
      }
    }, console.error);
    return () => unsub();
  }, [user]);

  // Chat corrente
  useEffect(() => {
    if (!user || !activeTab) return;
    const ref = doc(db, 'artifacts', APP_ID, 'users', user.uid, 'chats', activeTab);
    const unsub = onSnapshot(ref, (snap) => {
      setMessages(snap.exists() ? snap.data().history : []);
    }, console.error);
    return () => unsub();
  }, [user, activeTab]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // Cookie banner — mostra se non ancora scelto
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) setShowCookieBanner(true);
  }, []);

  // PWA install prompt
  useEffect(() => {
    const ios = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
    const isStandalone = window.navigator.standalone === true;
    setIsIOS(ios);

    // Android: intercetta evento beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Mostra banner PWA dopo 3 consulti (solo se non già installata)
  useEffect(() => {
    const count = userData.messageCount || 0;
    const alreadyShown = localStorage.getItem('pwaPromptShown');
    const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
    if (count >= 3 && !alreadyShown && !isStandalone) {
      setShowPWABanner(true);
      localStorage.setItem('pwaPromptShown', 'true');
    }
  }, [userData.messageCount]);

  // Mostra popup profilo dopo 2 consulti se non ha ancora nickname/dob
  // Si ripropone ogni 3 consulti
  useEffect(() => {
    if (!userData.nickname && !userData.dob) {
      const count = userData.messageCount || 0;
      if (count === 2 || (count > 2 && (count - 2) % 3 === 0)) {
        setShowProfilePopup(true);
      }
    }
  }, [userData.messageCount, userData.nickname, userData.dob]);

  // ─── AI ──────────────────────────────────────────────────────────────────────
  const askGemini = async (userPrompt) => {
    if (!GEMINI_API_KEY) return "Chiave API mancante. Configura REACT_APP_GEMINI_API_KEY su Vercel e rideploya.";
    const zodiacSign = getZodiacSign(userData.dob);
    const today = new Date().toLocaleDateString('it-IT');
    const luckyNumbers = getLuckyNumbers(userData.dob, today);
    const systemPrompt = getSystemPrompt(activeTab, userData.nickname, zodiacSign, userData.dob, today, luckyNumbers);
    try {
      const res = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ contents: [{ parts: [{ text: userPrompt }] }], systemInstruction: { parts: [{ text: systemPrompt }] } }) }
      );
      const data = await res.json();
      if (data.error) return `Errore: ${data.error.message}`;
      return data.candidates?.[0]?.content?.parts?.[0]?.text || "Le stelle oggi sono silenziose.";
    } catch (e) { return "Qualcosa ha disturbato la connessione. Riprova tra un istante."; }
  };

  // ─── INVIO MESSAGGIO ─────────────────────────────────────────────────────────
  const handleSendMessage = async (forcedText = null, isWelcome = false) => {
    const msg = forcedText || input.trim();
    if (!user || !msg || loading) return;
    const updated = [...messages, { role: 'user', text: msg }];
    setInput(''); setMessages(updated); setLoading(true);
    const aiRes = await askGemini(msg);
    const final = [...updated, { role: 'ai', text: aiRes }];
    try {
      await setDoc(doc(db, 'artifacts', APP_ID, 'users', user.uid, 'chats', activeTab), { history: final }, { merge: true });
      if (!isWelcome) await updateDoc(doc(db, 'artifacts', APP_ID, 'users', user.uid, 'settings', 'profile'), { messageCount: increment(1) });
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // ─── SALVA PROFILO (nickname + data) ─────────────────────────────────────────
  const saveProfile = async () => {
    const { nickname, day, month, year } = profileData;
    if (!nickname || !day || !month || !year || !user) return;
    const d = day.toString().padStart(2, '0');
    const m = month.toString().padStart(2, '0');
    const dob = `${year}-${m}-${d}`;
    await updateDoc(doc(db, 'artifacts', APP_ID, 'users', user.uid, 'settings', 'profile'), { nickname, dob });
    setShowProfilePopup(false);
    handleSendMessage(`Saluti, sono ${nickname}, nato il ${d}/${m}/${year}. Accoglimi.`, true);
  };

  // ─── AUTH ────────────────────────────────────────────────────────────────────
  const handleGoogleLogin = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      setShowAuthModal(false);
    } catch (e) { setAuthError('Accesso con Google non riuscito. Riprova.'); }
    setAuthLoading(false);
  };

  const handleEmailAuth = async () => {
    setAuthError('');
    setAuthLoading(true);
    const { email, password } = authData;
    if (!email || !password) { setAuthError('Inserisci email e password.'); setAuthLoading(false); return; }
    try {
      if (authMode === 'register') {
        await createUserWithEmailAndPassword(auth, email, password);
        await updateDoc(doc(db, 'artifacts', APP_ID, 'users', auth.currentUser.uid, 'settings', 'profile'), { email });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      setShowAuthModal(false);
      setAuthData({ email: '', password: '' });
    } catch (e) {
      const msgs = { 'auth/email-already-in-use': 'Email già registrata.', 'auth/wrong-password': 'Password errata.', 'auth/user-not-found': 'Account non trovato.', 'auth/weak-password': 'Password troppo corta (min. 6 caratteri).', 'auth/invalid-email': 'Email non valida.' };
      setAuthError(msgs[e.code] || 'Errore. Riprova.');
    }
    setAuthLoading(false);
  };

  // ─── COOKIE CONSENT ─────────────────────────────────────────────────────────
  const acceptCookies = () => {
    localStorage.setItem('cookieConsent', 'all');
    setShowCookieBanner(false);
  };

  const rejectCookies = () => {
    localStorage.setItem('cookieConsent', 'necessary');
    setShowCookieBanner(false);
  };

  // ─── PWA INSTALL ─────────────────────────────────────────────────────────────
  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    setShowPWABanner(false);
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  const zodiacSign = getZodiacSign(userData.dob);

  return (
    <div style={{background: 'linear-gradient(160deg, #F8F7F4 0%, #F0EBF8 50%, #F8F4EC 100%)', minHeight: '100vh', fontFamily: "'Inter', sans-serif"}} className="flex flex-col items-center">

      {/* HEADER */}
      <header className="w-full bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" style={{color: '#C9A84C'}} />
              <span className="font-bold text-lg tracking-tight" style={{color: '#1A1A2E'}}>Mystica Aeterna</span>
            </div>
            <span className="text-[10px] font-medium" style={{color: '#C9A84C', marginLeft: '28px'}}>Ogni giorno, la tua guida personale</span>
          </div>
          <div className="flex items-center gap-3">
            {userData.nickname && zodiacSign && (
              <span className="text-xs font-medium px-3 py-1 rounded-full" style={{background: '#F3EEFF', color: '#7C5CBF'}}>{zodiacSign}</span>
            )}
            {user && !user.isAnonymous ? (
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold" style={{color: '#6B7280'}}>{userData.nickname || user.email}</span>
                <button onClick={() => signOut(auth)} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors">
                  <LogOut className="w-4 h-4" style={{color: '#6B7280'}} />
                </button>
              </div>
            ) : (
              <button onClick={() => { setShowAuthModal(true); setAuthError(''); setAuthData({ email: '', password: '' }); }} className="text-xs font-semibold px-4 py-2 rounded-full transition-all hover:opacity-90" style={{background: '#1A1A2E', color: 'white'}}>
                Accedi
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="w-full max-w-2xl px-4 py-8">

        {/* Saluto */}
        {userData.nickname && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold" style={{color: '#1A1A2E'}}>Ciao, {userData.nickname} ✦</h2>
            <p className="text-sm mt-1" style={{color: '#6B7280'}}>{new Date().toLocaleDateString('it-IT', {weekday: 'long', day: 'numeric', month: 'long'})}</p>
          </div>
        )}

        {/* Tabs */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex flex-col items-center gap-2 py-5 px-3 rounded-2xl transition-all text-center" style={{ background: isActive ? tab.bg : 'white', border: isActive ? `2px solid ${tab.color}` : '2px solid transparent', boxShadow: isActive ? `0 4px 20px ${tab.color}25` : '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{background: isActive ? tab.color : '#F3F4F6'}}>
                  <Icon className="w-5 h-5" style={{color: isActive ? 'white' : '#9CA3AF'}} />
                </div>
                <span className="text-xs font-bold" style={{color: isActive ? tab.color : '#6B7280'}}>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Chat */}
        <div className="rounded-3xl overflow-hidden" style={{background: 'white', boxShadow: '0 4px 24px rgba(0,0,0,0.08)'}}>
          <div className="px-6 py-4 flex items-center gap-3" style={{background: currentTab.bg, borderBottom: `1px solid ${currentTab.color}20`}}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{background: currentTab.color}}>
              <TabIcon className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold" style={{color: '#1A1A2E'}}>{currentTab.name}</p>
              <p className="text-xs" style={{color: '#6B7280'}}>{currentTab.desc}</p>
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-xs font-medium" style={{color: '#6B7280'}}>Online</span>
            </div>
          </div>

          <div className="overflow-y-auto p-6 flex flex-col gap-4 custom-scrollbar" style={{minHeight: '380px', maxHeight: '440px'}}>
            {messages.length === 0 ? (
              <div className="m-auto text-center max-w-xs py-6">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{background: currentTab.bg}}>
                  <TabIcon className="w-8 h-8" style={{color: currentTab.color}} />
                </div>
                <p className="text-sm font-medium mb-1" style={{color: '#1A1A2E'}}>{currentTab.longDesc}</p>
                <p className="text-xs mb-6" style={{color: '#9CA3AF'}}>Scegli una domanda o scrivi la tua</p>
                <div className="space-y-2 text-left">
                  {currentTab.suggestions.map((sug, i) => (
                    <button key={i} onClick={() => handleSendMessage(sug)} className="w-full p-3 rounded-xl text-left text-xs font-medium flex items-center justify-between group transition-all hover:shadow-md" style={{background: currentTab.bg, color: '#1A1A2E', border: `1px solid ${currentTab.color}30`}}>
                      {sug}
                      <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 ml-2 opacity-40 group-hover:opacity-100 transition-opacity" style={{color: currentTab.color}} />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {m.role === 'ai' && (
                    <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center mr-2 mt-1" style={{background: currentTab.color}}>
                      <Sparkles className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <div className="max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed" style={m.role === 'user' ? { background: '#1A1A2E', color: 'white', borderBottomRightRadius: '4px' } : { background: currentTab.bg, color: '#1A1A2E', border: `1px solid ${currentTab.color}20`, borderBottomLeftRadius: '4px' }}>
                    <p className="whitespace-pre-wrap">{m.text}</p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex items-center gap-2 ml-9">
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{background: currentTab.color, animationDelay: '0ms'}} />
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{background: currentTab.color, animationDelay: '150ms'}} />
                  <span className="w-2 h-2 rounded-full animate-bounce" style={{background: currentTab.color, animationDelay: '300ms'}} />
                </div>
                <span className="text-xs" style={{color: '#9CA3AF'}}>L'Oracolo sta rispondendo...</span>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <div className="p-4 border-t" style={{borderColor: '#F3F4F6'}}>
            <div className="flex items-center gap-3 p-2 rounded-2xl" style={{background: '#F8F7F4'}}>
              <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()} placeholder={activeTab === 'futuro' ? "Fai la tua domanda..." : "Scrivi qui..."} disabled={loading || !user} className="flex-1 bg-transparent text-sm outline-none px-2" style={{color: '#1A1A2E'}} />
              <button onClick={() => handleSendMessage()} disabled={loading || !user || !input.trim()} className="w-9 h-9 rounded-xl flex items-center justify-center transition-all active:scale-90 disabled:opacity-30" style={{background: currentTab.color}}>
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="w-full max-w-2xl px-4 py-6 mt-2 flex flex-col items-center gap-2">
        <div className="flex items-center gap-4 text-xs" style={{color: '#9CA3AF'}}>
          <span>© {new Date().getFullYear()} Events' Raiders S.r.l.</span>
          <span>·</span>
          <button onClick={() => setShowPrivacy(true)} className="hover:underline" style={{color: '#C9A84C'}}>Privacy Policy</button>
          <span>·</span>
          <a href="mailto:privacy@mystica-aeterna.com" className="hover:underline" style={{color: '#9CA3AF'}}>Contatti</a>
        </div>
        <p className="text-xs text-center" style={{color: '#C9A84C', opacity: 0.7}}>Le consultazioni hanno scopo ludico e di intrattenimento. Non sostituiscono il parere di professionisti.</p>
      </footer>

      {/* ── POPUP PROFILO (nickname + data) ──────────────────────────────────── */}
      {showProfilePopup && (
        <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center" style={{background: 'rgba(26,26,46,0.5)', backdropFilter: 'blur(4px)'}}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{background: '#FFF8EC'}}>
                  <Star className="w-4 h-4" style={{color: '#C9A84C'}} />
                </div>
                <p className="font-bold text-sm" style={{color: '#1A1A2E'}}>Letture più precise per te</p>
              </div>
              <button onClick={() => setShowProfilePopup(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="w-4 h-4" style={{color: '#9CA3AF'}} />
              </button>
            </div>
            <p className="text-xs mb-4" style={{color: '#6B7280'}}>Dimmi il tuo nome e quando sei nato — l'Oracolo ti risponderà in modo molto più preciso.</p>
            <div className="space-y-3 mb-4">
              <input type="text" value={profileData.nickname} onChange={(e) => setProfileData({...profileData, nickname: e.target.value})} placeholder="Il tuo nome o nickname" style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#C9A84C'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              <div>
                <p className="text-xs font-semibold mb-1.5" style={{color: '#6B7280'}}>Data di nascita</p>
                <div className="flex gap-2">
                  <input type="number" value={profileData.day} onChange={(e) => setProfileData({...profileData, day: e.target.value})} placeholder="GG" min="1" max="31" style={{...inputStyle, textAlign: 'center', width: '30%'}} onFocus={(e) => e.target.style.borderColor = '#C9A84C'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
                  <input type="number" value={profileData.month} onChange={(e) => setProfileData({...profileData, month: e.target.value})} placeholder="MM" min="1" max="12" style={{...inputStyle, textAlign: 'center', width: '30%'}} onFocus={(e) => e.target.style.borderColor = '#C9A84C'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
                  <input type="number" value={profileData.year} onChange={(e) => setProfileData({...profileData, year: e.target.value})} placeholder="AAAA" min="1900" max="2099" style={{...inputStyle, textAlign: 'center', width: '40%'}} onFocus={(e) => e.target.style.borderColor = '#C9A84C'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
                </div>
              </div>
            </div>
            <button onClick={saveProfile} disabled={!profileData.nickname || !profileData.day || !profileData.month || !profileData.year} className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-30 mb-2" style={{background: 'linear-gradient(135deg, #C9A84C, #B8962D)', color: 'white'}}>
              Personalizza la mia lettura
            </button>
            <button onClick={() => setShowProfilePopup(false)} className="w-full py-2 text-xs font-medium" style={{color: '#9CA3AF'}}>Non ora</button>
          </div>
        </div>
      )}

      {/* ── MODALE AUTH (Accedi / Registrati) ───────────────────────────────── */}
      {showAuthModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{background: 'rgba(26,26,46,0.7)', backdropFilter: 'blur(8px)'}}>
          <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold" style={{color: '#1A1A2E'}}>{authMode === 'login' ? 'Accedi' : 'Crea account'}</h2>
              <button onClick={() => setShowAuthModal(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="w-5 h-5" style={{color: '#9CA3AF'}} />
              </button>
            </div>

            {/* Google */}
            <button onClick={handleGoogleLogin} disabled={authLoading} className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-3 mb-4 transition-all hover:shadow-md disabled:opacity-50" style={{background: 'white', border: '1.5px solid #E5E7EB', color: '#1A1A2E'}}>
              <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
              Continua con Google
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px" style={{background: '#E5E7EB'}} />
              <span className="text-xs" style={{color: '#9CA3AF'}}>oppure</span>
              <div className="flex-1 h-px" style={{background: '#E5E7EB'}} />
            </div>

            {/* Email + Password */}
            <div className="space-y-3 mb-4">
              <input type="email" value={authData.email} onChange={(e) => setAuthData({...authData, email: e.target.value})} placeholder="Email" style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#C9A84C'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
              <input type="password" value={authData.password} onChange={(e) => setAuthData({...authData, password: e.target.value})} onKeyPress={(e) => e.key === 'Enter' && handleEmailAuth()} placeholder="Password" style={inputStyle} onFocus={(e) => e.target.style.borderColor = '#C9A84C'} onBlur={(e) => e.target.style.borderColor = '#E5E7EB'} />
            </div>

            {authError && <p className="text-xs text-red-500 mb-3 text-center">{authError}</p>}

            <button onClick={handleEmailAuth} disabled={authLoading} className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-50 mb-4" style={{background: 'linear-gradient(135deg, #C9A84C, #B8962D)', color: 'white'}}>
              {authLoading ? 'Attendere...' : authMode === 'login' ? 'Accedi' : 'Crea account'}
            </button>

            <p className="text-xs text-center" style={{color: '#6B7280'}}>
              {authMode === 'login' ? 'Non hai un account? ' : 'Hai già un account? '}
              <button onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError(''); }} className="font-semibold hover:underline" style={{color: '#C9A84C'}}>
                {authMode === 'login' ? 'Registrati' : 'Accedi'}
              </button>
            </p>

            {authMode === 'register' && (
              <p className="text-xs text-center mt-3" style={{color: '#9CA3AF'}}>
                Registrandoti riceverai il tuo oroscopo ogni mattina ✦
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── MODALE PRIVACY ───────────────────────────────────────────────────── */}
      {showPrivacy && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{background: 'rgba(26,26,46,0.8)', backdropFilter: 'blur(8px)'}}>
          <div className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl flex flex-col" style={{maxHeight: '85vh'}}>
            <div className="flex items-center justify-between px-8 py-5 border-b" style={{borderColor: '#F3F4F6'}}>
              <h2 className="text-lg font-bold" style={{color: '#1A1A2E'}}>Informativa sulla Privacy</h2>
              <button onClick={() => setShowPrivacy(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors text-gray-400 font-bold text-lg">×</button>
            </div>
            <div className="overflow-y-auto px-8 py-6 text-sm leading-relaxed custom-scrollbar" style={{color: '#374151'}}>
              <p className="text-xs mb-6" style={{color: '#9CA3AF'}}>Ai sensi del Regolamento UE 2016/679 (GDPR) — Sito web: www.mystica-aeterna.com</p>
              <h3 className="font-bold text-base mb-2" style={{color: '#1A1A2E'}}>1. Titolare del Trattamento</h3>
              <p className="mb-1">Events' Raiders S.r.l.</p>
              <p className="mb-1">Via Terraglio 15, 42048 Rubiera (RE)</p>
              <p className="mb-1">P.I. IT02753890355</p>
              <p className="mb-4">Email: <a href="mailto:privacy@mystica-aeterna.com" className="underline" style={{color: '#C9A84C'}}>privacy@mystica-aeterna.com</a></p>
              <h3 className="font-bold text-base mb-2" style={{color: '#1A1A2E'}}>2. Dati Personali Raccolti</h3>
              <p className="mb-2">Raccogliamo i seguenti dati forniti volontariamente dall'utente:</p>
              <ul className="list-disc ml-5 mb-4 space-y-1"><li>Nome o nickname</li><li>Data di nascita</li><li>Indirizzo email</li></ul>
              <p className="mb-4">Raccogliamo inoltre dati tecnici automaticamente (indirizzo IP, browser, pagine visitate) tramite cookie tecnici e strumenti di analisi.</p>
              <h3 className="font-bold text-base mb-2" style={{color: '#1A1A2E'}}>3. Finalità e Base Giuridica</h3>
              <p className="mb-2"><strong>Erogazione del servizio:</strong> i dati di registrazione sono necessari per le consultazioni personalizzate. Base giuridica: esecuzione del contratto (art. 6 lett. b GDPR).</p>
              <p className="mb-2"><strong>Comunicazioni email:</strong> l'indirizzo email potrà essere usato per oroscopi quotidiani. Base giuridica: consenso (art. 6 lett. a GDPR), revocabile in qualsiasi momento.</p>
              <p className="mb-4"><strong>Pubblicità (Google AdSense):</strong> il sito utilizza Google AdSense per annunci pubblicitari. Google potrebbe raccogliere dati tramite cookie. Info: <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline" style={{color: '#C9A84C'}}>policies.google.com/privacy</a>.</p>
              <h3 className="font-bold text-base mb-2" style={{color: '#1A1A2E'}}>4. Periodo di Conservazione</h3>
              <ul className="list-disc ml-5 mb-4 space-y-1"><li>Dati di registrazione: fino a 2 anni dalla cancellazione dell'account.</li><li>Dati per email: fino alla revoca del consenso.</li><li>Dati tecnici: massimo 12 mesi.</li></ul>
              <h3 className="font-bold text-base mb-2" style={{color: '#1A1A2E'}}>5. Condivisione con Terze Parti</h3>
              <ul className="list-disc ml-5 mb-4 space-y-1"><li>Google LLC (Firebase, Gemini API, AdSense) — aderente al Data Privacy Framework EU-USA.</li><li>Fornitori tecnici vincolati da accordi di riservatezza.</li><li>Autorità competenti, ove richiesto dalla legge.</li></ul>
              <h3 className="font-bold text-base mb-2" style={{color: '#1A1A2E'}}>6. Diritti dell'Interessato</h3>
              <p className="mb-4">Ai sensi degli artt. 15-22 GDPR, l'utente ha diritto di accedere, rettificare, cancellare i propri dati, limitare o opporsi al trattamento, ricevere i dati in formato portabile, revocare il consenso. Per esercitare i diritti: <a href="mailto:privacy@mystica-aeterna.com" className="underline" style={{color: '#C9A84C'}}>privacy@mystica-aeterna.com</a>.</p>
              <h3 className="font-bold text-base mb-2" style={{color: '#1A1A2E'}}>7. Cookie</h3>
              <ul className="list-disc ml-5 mb-4 space-y-1"><li><strong>Tecnici:</strong> necessari al funzionamento. Non richiedono consenso.</li><li><strong>Pubblicitari:</strong> Google AdSense. Richiedono consenso.</li><li><strong>Analitici:</strong> statistiche anonime.</li></ul>
              <h3 className="font-bold text-base mb-2" style={{color: '#1A1A2E'}}>8. Minori</h3>
              <p className="mb-6">Il servizio è rivolto a utenti maggiorenni. Non raccogliamo consapevolmente dati di minori.</p>
              <p className="text-xs" style={{color: '#9CA3AF'}}>Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT', {day: 'numeric', month: 'long', year: 'numeric'})}</p>
            </div>
            <div className="px-8 py-4 border-t" style={{borderColor: '#F3F4F6'}}>
              <button onClick={() => setShowPrivacy(false)} className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95" style={{background: 'linear-gradient(135deg, #C9A84C, #B8962D)', color: 'white'}}>Ho capito</button>
            </div>
          </div>
        </div>
      )}

      {/* ── COOKIE BANNER ──────────────────────────────────────────────────── */}
      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[300] p-4" style={{background: 'white', borderTop: '1px solid #E5E7EB', boxShadow: '0 -4px 24px rgba(0,0,0,0.08)'}}>
          <div className="max-w-2xl mx-auto">
            <p className="text-xs mb-3" style={{color: '#374151'}}>
              🍪 Utilizziamo cookie tecnici e pubblicitari (Google AdSense) per migliorare la tua esperienza e mostrarti annunci pertinenti. Leggi la nostra{' '}
              <button onClick={() => setShowPrivacy(true)} className="underline font-medium" style={{color: '#C9A84C'}}>Privacy Policy</button>.
            </p>
            <div className="flex gap-2">
              <button onClick={acceptCookies} className="flex-1 py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95" style={{background: 'linear-gradient(135deg, #C9A84C, #B8962D)', color: 'white'}}>
                Accetta tutti
              </button>
              <button onClick={rejectCookies} className="flex-1 py-2.5 rounded-xl text-xs font-bold transition-all" style={{background: '#F3F4F6', color: '#6B7280'}}>
                Solo necessari
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── PWA INSTALL BANNER ──────────────────────────────────────────────── */}
      {showPWABanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[290] p-4" style={{background: 'white', borderTop: '1px solid #E5E7EB', boxShadow: '0 -4px 24px rgba(0,0,0,0.08)', marginBottom: showCookieBanner ? '120px' : '0'}}>
          <div className="max-w-2xl mx-auto">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{background: '#FFF8EC'}}>
                  <Sparkles className="w-4 h-4" style={{color: '#C9A84C'}} />
                </div>
                <div>
                  <p className="text-xs font-bold" style={{color: '#1A1A2E'}}>Aggiungi alla schermata home</p>
                  <p className="text-xs" style={{color: '#6B7280'}}>Accedi ogni giorno con un solo tocco</p>
                </div>
              </div>
              <button onClick={() => setShowPWABanner(false)} className="p-1 rounded-full hover:bg-gray-100">
                <X className="w-4 h-4" style={{color: '#9CA3AF'}} />
              </button>
            </div>
            {isIOS ? (
              <p className="text-xs p-3 rounded-xl" style={{background: '#F8F7F4', color: '#374151'}}>
                Tocca <strong>⎙</strong> in basso nel browser, poi seleziona <strong>"Aggiungi a schermata Home"</strong>
              </p>
            ) : (
              <button onClick={handleInstallPWA} className="w-full py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95" style={{background: 'linear-gradient(135deg, #C9A84C, #B8962D)', color: 'white'}}>
                Installa l'app
              </button>
            )}
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 10px; }
        @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
        .animate-bounce { animation: bounce 1s infinite; }
        input[type=number]::-webkit-inner-spin-button, input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
      `}</style>
    </div>
  );
};

export default App;
